import React from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Form } from 'antd'
import styled from 'styled-components'
import { pathOr, keys, values } from 'ramda'
import { read, utils } from 'xlsx'
import { InboxOutlined, FileTextOutlined } from '@ant-design/icons'
import Box from '@shared/Box'
import Button from '@shared/Form/Button'
import { Column } from '@shared/Layout'
import Modal from '@shared/Modal'
import { rules } from '@shared/utils/form'
import { message } from '@shared/innmaxUI/Message/Message'

import { useXlsxFormat } from '@services/device'
import DraggerBox from 'src/components/DraggerBox'

type Props = {
  loading?: boolean
  deviceType: string
  csvFormatString?: string
  handleImport: ({ file }: { file: FileList | null }) => any
  onCancel: () => void
}

type FileProps = File

function ImportModal({ handleImport, onCancel, loading = false, deviceType }: Props) {
  const { t } = useTranslation()

  const [form] = Form.useForm()

  const [imagePreview, setImagePreview] = useState<FileProps>()

  const [uri, setUri] = useState<string | undefined>()

  const [xlsxFormatString, setXlsxFormatString] = useState('')

  const [isLoading, mutate] = useXlsxFormat(
    { deviceType },
    {
      onSuccess: (res: any) => {
        setUri(window.URL.createObjectURL(res?.response))
      }
    }
  )

  const handleOk = () => {
    form.validateFields().then(values => {
      const file = imagePreview ? pathOr(null, ['dragger', 0, 'originFileObj'], values) : null
      if (!file) {
        console.error('file is null')
        return
      }
      handleImport({ file }).then(
        ()=>{
          onCancel()
        }
      )
    })
  }

  const customRequest = () => {
    return false
  }

  const handleDownloadSampleFile = () => {
    if (uri) {
      const element = document.createElement('a')
      element.href = uri
      element.download = `${deviceType}-template.xlsx`
      document.body.appendChild(element) // Required for this to work in FireFox
      element.click()
    }
  }

  const normFile = (e: any) => {
    if (Array.isArray(e)) {
      return e
    }

    setImagePreview(e.file)
    return e && e.fileList
  }

  const copyClipboard = () => {
    navigator.clipboard.writeText(xlsxFormatString).then(() => {
      message({
        content: '已複製範例內容',
        type: 'success',
      })
    })
  }

  React.useEffect(() => {
    mutate({ deviceType })
  }, []) //eslint-disable-line

  React.useEffect(() => {
    if (uri) {
      fetch(uri)
        .then(res => res.arrayBuffer())
        .then(f => {
          const wb = read(f)
          const data = utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]])
          setXlsxFormatString(
            `${keys(pathOr([], [0], data)).join(', ')} ${values(pathOr([], [0], data)).join(', ')}`
          )
        })
    }
  }, [uri])

  return (
    <Modal
      open
      width={640}
      title={t('light:import')}
      onCancel={onCancel}
      centered
      bodyStyle={{ paddingLeft: 40, paddingTop: 34, paddingBottom: 24 }}
      footer={[
        <Button
          key="btn-cancel"
          size="small"
          type="primary"
          ghost
          style={{ minWidth: 96, marginRight: 8 }}
          onClick={onCancel}>
          {t('common:cancel')}
        </Button>,
        <Button
          key="btn-ok"
          size="small"
          type="primary"
          style={{ minWidth: 96 }}
          loading={loading}
          onClick={handleOk}>
          {t('common:upload')}
        </Button>,
      ]}>
      <Form form={form} requiredMark={false} colon={false} labelAlign="left">
        <Form.Item label={<Label>{t('common:data sample')}</Label>}>
          {
            xlsxFormatString && (
              <Column.LightGrey style={{ width: '100%' }}>
                <span onClick={copyClipboard}>{xlsxFormatString}</span>
              </Column.LightGrey>
            )
          }
          <Button
            style={{ minWidth: 'unset', padding: 0 }}
            type="link"
            loading={isLoading}
            onClick={handleDownloadSampleFile}>
            {t('common:sample download')}
          </Button>
        </Form.Item>
        <Form.Item
          name="dragger"
          rules={[rules.required]}
          label={<Label>{t('common:upload file')}</Label>}
          valuePropName="fileList"
          getValueFromEvent={normFile}
          className="mt-40 mb-24">
          <DraggerBox
            accept=".xlsx"
            multiple={false}
            showUploadList={false}
            customRequest={customRequest}
            style={{ minWidth: 80, width: '80%' }}>
            <Box marginBottom="30px">
              <p
                style={{
                  color: '#4A4A4A',
                  fontSize: '50px',
                }}>
                {imagePreview ? <FileTextOutlined /> : <InboxOutlined />}
              </p>
            </Box>

            {imagePreview ? (
              <p className="ant-upload-text">{imagePreview.name}</p>
            ) : (
              <p className="ant-upload-text">{t('system:drag here')}</p>
            )}
            <p className="ant-upload-hint">{t('common:support .xlsx')}</p>
          </DraggerBox>
        </Form.Item>
      </Form>
    </Modal>
  )
}

const Label = styled.div`
  font-size: 16px;
  color: #4a4a4a;
  margin-right: 14px;
`

export default ImportModal
